import React, { useState, useEffect, useRef } from 'react';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import './ReservedDashboard.css';
import { Donor, DonorPhase } from '../../../../shared/models/donor';
import ManageNews from './ManageNews';

import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { printModule } from '../../utils/printUtils';
import { searchComuni } from 'italian-locations';
import { SurveyManager } from './SurveyManager';
import { logAction } from '../../api/loggingService';

interface UserInfo {
    clientPrincipal: {
        userId: string;
        userDetails: string;
        userRoles: string[];
    } | null;
}

const donorFields = [
    { name: 'firstName', label: 'Nome', type: 'text', required: true },
    { name: 'lastName', label: 'Cognome', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'gender', label: 'Sesso', type: 'select', options: [{ label: 'Maschio', value: 'male' }, { label: 'Femmina', value: 'female' }] },
    { name: 'birthDate', label: 'Data di Nascita', type: 'date' },
    { name: 'birthPlace', label: 'Luogo di Nascita', type: 'text' },
    { name: 'birthProvince', label: 'Provincia di Nascita', type: 'text' },
    { name: 'taxCode', label: 'Codice Fiscale', type: 'text' },
    { name: 'phone', label: 'Telefono', type: 'text' },
    { name: 'town', label: 'Comune di residenza', type: 'text' },
    { name: 'province', label: 'Provincia di residenza', type: 'text' },
    { name: 'address', label: 'Indirizzo di residenza', type: 'text' },
    { name: 'domicileTown', label: 'Comune di domicilio', type: 'text' },
    { name: 'domicileAddress', label: 'Indirizzo di domicilio', type: 'text' },
    {
        name: 'education', label: 'Titolo di Studio', type: 'select', options: [
            { label: 'Nessuno', value: 'none' },
            { label: 'Licenza Elementare', value: 'primary_school' },
            { label: 'Licenza Media Inferiore', value: 'middle_school' },
            { label: 'Diploma', value: 'diploma' },
            { label: 'Laurea', value: 'degree' }
        ]
    },
    {
        name: 'profession', label: 'Professione', type: 'select', options: [
            { label: 'Agricoltore', value: 'farmer' },
            { label: 'Artigiano', value: 'artisan' },
            { label: 'Commerciante', value: 'merchant' },
            { label: 'Impiegato', value: 'employee' },
            { label: 'Insegnante', value: 'teacher' },
            { label: 'Operaio', value: 'worker' },
            { label: 'Professionista', value: 'professional' },
            { label: 'Militare', value: 'military' },
            { label: 'Religioso', value: 'religious' },
            { label: 'Altro', value: 'other' }
        ]
    },
    {
        name: 'nonProfessionalCondition', label: 'Condizione non professionale', type: 'select', options: [
            { label: 'Disoccupato', value: 'unemployed' },
            { label: 'Studente', value: 'student' },
            { label: 'Casalinga', value: 'housewife' },
            { label: 'Pensionato', value: 'pensioner' }
        ]
    },
    {
        name: 'donationPreferences', label: 'Preferenze Donazione', type: 'select', options: [
            { label: 'Lunedì', value: 'monday' },
            { label: 'Martedì', value: 'tuesday' },
            { label: 'Mercoledì', value: 'wednesday' },
            { label: 'Giovedì', value: 'thursday' },
            { label: 'Venerdì', value: 'friday' },
            { label: 'Sabato', value: 'saturday' },
            { label: 'Domenica', value: 'sunday' }
        ]
    },
    { name: 'otherAssociations', label: 'Altre Associazioni', type: 'text' },
    { name: 'localAvis', label: 'AVIS Comunale', type: 'select', options: [{ label: 'Merate', value: 'Merate' }, { label: 'Brivio', value: 'Brivio' }, { label: 'Missaglia', value: 'Missaglia' }] },
];

/** Maps phase value to a display configuration */
const PHASE_CONFIG: Record<DonorPhase, { label: string; icon: string; color: string; severity: 'info' | 'warning' | 'success' | 'secondary' }> = {
    non_convocato: { label: 'Non ancora convocato', icon: 'pi pi-clock', color: '#6366f1', severity: 'info' },
    convocato: { label: 'Convocato', icon: 'pi pi-envelope', color: '#f59e0b', severity: 'warning' },
    idoneita: { label: 'Idoneità', icon: 'pi pi-check-circle', color: '#22c55e', severity: 'success' },
    gestito_esternamente: { label: 'Gestito Fuori Portale', icon: 'pi pi-check-square', color: '#64748b', severity: 'secondary' },
};

const ReservedDashboard: React.FC = () => {
    const [activeSection, setActiveSection] = useState<'candidati' | 'notizie' | 'sondaggi'>('candidati');
    const [user, setUser] = useState<{ details: string, roles: string[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [donors, setDonors] = useState<Donor[]>([]);
    const [loadingDonors, setLoadingDonors] = useState(false);
    const [donorDialog, setDonorDialog] = useState(false);
    const [selectedDonor, setSelectedDonor] = useState<Donor | null>(null);
    const [sendingConvocation, setSendingConvocation] = useState<string | null>(null);
    const [savingDonor, setSavingDonor] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [barcodeFilter, setBarcodeFilter] = useState('');
    const [markingIdoneita, setMarkingIdoneita] = useState<string | null>(null);
    const [markingExternal, setMarkingExternal] = useState<string | null>(null);
    const toast = useRef<Toast>(null);
    const barcodeInputRef = useRef<HTMLInputElement>(null);


    const hasRole = (role: string) => user?.roles.includes('admin') || user?.roles.includes(role);

    // Derive the phase from existing data for backward compatibility
    const getDonorPhase = (donor: Donor): DonorPhase => {
        if (donor.phase) return donor.phase;
        if (donor.convocationStatus === 'sent') return 'convocato';
        return 'non_convocato';
    };

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await fetch('/.auth/me');
                const data: UserInfo = await response.json();

                if (data.clientPrincipal && (
                    data.clientPrincipal.userRoles.includes('admin') ||
                    data.clientPrincipal.userRoles.includes('news-editor') ||
                    data.clientPrincipal.userRoles.includes('candidates-manager')
                )) {
                    setUser({
                        details: data.clientPrincipal.userDetails,
                        roles: data.clientPrincipal.userRoles
                    });

                    // Set initial section based on roles
                    if (!data.clientPrincipal.userRoles.includes('admin') &&
                        !data.clientPrincipal.userRoles.includes('candidates-manager') &&
                        data.clientPrincipal.userRoles.includes('news-editor')) {
                        setActiveSection('notizie');
                    }

                } else {
                    window.location.href = '/login';
                }
            } catch (error) {
                console.error('Auth check failed', error);
                window.location.href = '/login';
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    const fetchDonors = async () => {
        setLoadingDonors(true);
        try {
            const res = await fetch('/api/getDonors');
            if (res.ok) {
                const data = await res.json();
                setDonors(data);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Errore Caricamento',
                    detail: `Impossibile recuperare gli aspiranti (Status: ${res.status})`,
                    life: 5000
                });
            }
        } catch (error) {
            console.error("Failed to fetch donors", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Errore di Rete',
                detail: 'Controlla la tua connessione e riprova.',
                life: 5000
            });
        } finally {
            setLoadingDonors(false);
        }
    };

    useEffect(() => {
        if (user && activeSection === 'candidati' && hasRole('candidates-manager')) {
            fetchDonors();
        }
    }, [user, activeSection]);

    const saveDonor = async () => {
        setSavingDonor(true);
        try {
            const res = await fetch('/api/updateDonor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(selectedDonor)
            });

            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Successo', detail: 'Aspirante salvato' });
                setDonorDialog(false);
                fetchDonors();
            } else {
                const err = await res.text();
                toast.current?.show({ severity: 'error', summary: 'Errore', detail: err });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        } finally {
            setSavingDonor(false);
        }
    };

    const sendConvocation = async (rowData: any) => {
        // Find the latest donor data from state to be sure we have the updated date
        const donor = donors.find(d => d.email === rowData.email) || rowData;

        if (!donor.convocationDate) {
            toast.current?.show({ severity: 'warn', summary: 'Attenzione', detail: 'Inserire prima la data della convocazione' });
            return;
        }

        setSendingConvocation(donor.email);
        try {
            // First update the donor in DB to ensure the date is saved
            await fetch('/api/updateDonor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...donor, phase: 'convocato' })
            });

            // Then send the email
            const res = await fetch('/api/sendConvocation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: donor.email, convocationDate: donor.convocationDate })
            });

            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Inviato', detail: 'Convocazione inviata con successo' });
                fetchDonors();
            } else {
                const err = await res.text();
                toast.current?.show({ severity: 'error', summary: 'Errore', detail: 'Invio fallito: ' + err });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete nell\'invio' });
        } finally {
            setSendingConvocation(null);
        }
    };

    const markAsIdoneita = async (rowData: Donor) => {
        setMarkingIdoneita(rowData.email);
        try {
            const res = await fetch('/api/updateDonor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...rowData, phase: 'idoneita' })
            });

            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Successo', detail: `${rowData.firstName} ${rowData.lastName} spostato/a in Idoneità` });
                fetchDonors();
            } else {
                const err = await res.text();
                toast.current?.show({ severity: 'error', summary: 'Errore', detail: err });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        } finally {
            setMarkingIdoneita(null);
        }
    };

    const markAsExternal = async (rowData: Donor) => {
        setMarkingExternal(rowData.email);
        try {
            const res = await fetch('/api/markDonorExternal', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: rowData.email })
            });

            if (res.ok) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Gestito Fuori Portale',
                    detail: `${rowData.firstName} ${rowData.lastName} segnato/a come gestito al di fuori del portale`
                });
                fetchDonors();
            } else {
                const err = await res.text();
                toast.current?.show({ severity: 'error', summary: 'Errore', detail: err });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        } finally {
            setMarkingExternal(null);
        }
    };

    const updateDonorField = (name: string, value: any) => {

        setSelectedDonor((prev: any) => {
            const updated = { ...prev, [name]: value };

            // Auto-populate province based on city
            if (name === 'birthPlace' && value) {
                const results = searchComuni(value);
                if (results.length > 0) {
                    updated.birthProvince = results[0].item.siglaProvincia;
                }
            } else if (name === 'town' && value) {
                const results = searchComuni(value);
                if (results.length > 0) {
                    updated.province = results[0].item.siglaProvincia;
                }
            }

            return updated;
        });
    };

    const items = [
        {
            label: 'Gestionale',
            items: [
                {
                    label: 'Aspiranti',
                    icon: 'pi pi-users',
                    visible: hasRole('candidates-manager'),
                    className: activeSection === 'candidati' ? 'active-menu-item' : '',
                    command: () => setActiveSection('candidati')
                },
                {
                    label: 'Notizie',
                    icon: 'pi pi-megaphone',
                    visible: hasRole('news-editor'),
                    className: activeSection === 'notizie' ? 'active-menu-item' : '',
                    command: () => setActiveSection('notizie')
                },
                {
                    label: 'Sondaggi & Form',
                    icon: 'pi pi-file-edit',
                    visible: hasRole('news-editor') || hasRole('survey-manager') || hasRole('admin'),
                    className: activeSection === 'sondaggi' ? 'active-menu-item' : '',
                    command: () => setActiveSection('sondaggi')
                }
            ]
        },
        {
            label: 'Account',
            items: [
                {
                    label: 'Logout',
                    icon: 'pi pi-sign-out',
                    command: () => window.location.href = '/logout'
                }
            ]
        }
    ];

    const getStatusSeverity = (status?: string) => {
        switch (status) {
            case 'sent': return 'success';
            case 'error': return 'danger';
            case 'not_sent': return 'warning';
            default: return 'info';
        }
    };

    const getStatusLabel = (status?: string) => {
        switch (status) {
            case 'sent': return 'Inviata';
            case 'error': return 'Errore';
            case 'not_sent': return 'Non Inviata';
            default: return 'Nuovo';
        }
    };

    const convocationCell = (rowData: any) => {
        return (
            <div className="convocation-cell-container">
                <Calendar
                    value={rowData.convocationDate ? new Date(rowData.convocationDate) : null}
                    onChange={(e) => {
                        const newDonors = donors.map((d: any) => d.email === rowData.email ? { ...d, convocationDate: e.value } : d);
                        setDonors(newDonors);
                    }}
                    showTime
                    hourFormat="24"
                    placeholder="Data e Ora"
                    className="convocation-calendar p-inputtext-sm"
                    appendTo={document.body}
                />
                <Button
                    label="Invia"
                    icon="pi pi-send"
                    loading={sendingConvocation === rowData.email}
                    onClick={() => sendConvocation(rowData)}
                    severity="danger"
                    size="small"
                    className="convocation-button"
                />
            </div>
        );
    };

    const handlePrint = (donor: any) => {
        logAction('document_print', donor.email, { 
            donorName: `${donor.firstName} ${donor.lastName}` 
        });
        printModule(donor, 'completo');
    };

    const actionCell = (rowData: any) => {
        return (
            <div className="flex gap-2 align-items-center">
                <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={() => {
                    const donor = { ...rowData };
                    if (donor.birthDate) donor.birthDate = new Date(donor.birthDate);
                    setSelectedDonor(donor);
                    setDonorDialog(true);
                }} tooltip="Modifica" className="p-button-sm" />
                <Button icon="pi pi-print" rounded severity="danger" onClick={() => handlePrint(rowData)} tooltip="Stampa Modulo Completo" className="p-button-sm shadow-2" />
                <Button
                    icon="pi pi-check-square"
                    rounded
                    severity="help"
                    onClick={() => markAsExternal(rowData)}
                    loading={markingExternal === rowData.email}
                    tooltip="Segna aspirante come gestito al fuori del portale"
                    className="p-button-sm shadow-2"
                />
            </div>
        );
    };

    const idoneitaActionCell = (rowData: any) => {
        return (
            <div className="flex gap-2 align-items-center">
                <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={() => {
                    const donor = { ...rowData };
                    if (donor.birthDate) donor.birthDate = new Date(donor.birthDate);
                    setSelectedDonor(donor);
                    setDonorDialog(true);
                }} tooltip="Modifica" className="p-button-sm" />
                <Button icon="pi pi-print" rounded severity="danger" onClick={() => handlePrint(rowData)} tooltip="Stampa Modulo Completo" className="p-button-sm shadow-2" />
                <Button
                    icon="pi pi-check-square"
                    rounded
                    severity="help"
                    onClick={() => markAsExternal(rowData)}
                    loading={markingExternal === rowData.email}
                    tooltip="Segna aspirante come gestito al fuori del portale"
                    className="p-button-sm shadow-2"
                />
            </div>
        );
    };

    const convocatoActionCell = (rowData: any) => {
        return (
            <div className="flex gap-2 align-items-center">
                <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={() => {
                    const donor = { ...rowData };
                    if (donor.birthDate) donor.birthDate = new Date(donor.birthDate);
                    setSelectedDonor(donor);
                    setDonorDialog(true);
                }} tooltip="Modifica" className="p-button-sm" />
                <Button icon="pi pi-print" rounded severity="danger" onClick={() => handlePrint(rowData)} tooltip="Stampa Modulo Completo" className="p-button-sm shadow-2" />
                <Button
                    icon="pi pi-arrow-right"
                    rounded
                    severity="success"
                    onClick={() => markAsIdoneita(rowData)}
                    loading={markingIdoneita === rowData.email}
                    tooltip="Segna come Idoneità"
                    className="p-button-sm shadow-2"
                />
                <Button
                    icon="pi pi-check-square"
                    rounded
                    severity="help"
                    onClick={() => markAsExternal(rowData)}
                    loading={markingExternal === rowData.email}
                    tooltip="Segna aspirante come gestito al fuori del portale"
                    className="p-button-sm shadow-2"
                />
            </div>
        );
    };

    // Filter donors into phases
    const nonConvocatiDonors = donors.filter(d => getDonorPhase(d) === 'non_convocato');
    const convocatiDonors = donors.filter(d => getDonorPhase(d) === 'convocato');
    const idoneitaDonors = donors.filter(d => getDonorPhase(d) === 'idoneita');
    const gestitoEsternamenteDonors = donors.filter(d => getDonorPhase(d) === 'gestito_esternamente');

    // Apply barcode (tax code) filter
    const filterByBarcode = (list: Donor[]) => {
        if (!barcodeFilter.trim()) return list;
        const filter = barcodeFilter.trim().toUpperCase();
        return list.filter(d => d.taxCode?.toUpperCase().includes(filter));
    };

    const filteredNonConvocati = filterByBarcode(nonConvocatiDonors);
    const filteredConvocati = filterByBarcode(convocatiDonors);
    const filteredIdoneita = filterByBarcode(idoneitaDonors);
    const filteredGestitoEsternamente = filterByBarcode(gestitoEsternamenteDonors);


    // Tab header template with count badge
    const tabHeaderTemplate = (phase: DonorPhase, count: number) => {
        const config = PHASE_CONFIG[phase];
        return (
            <div className="tab-header-custom">
                <i className={`${config.icon} tab-header-icon`} style={{ color: config.color }}></i>
                <span className="tab-header-label">{config.label}</span>
                <span className="tab-header-badge" style={{ backgroundColor: config.color }}>{count}</span>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center h-screen">
                <ProgressSpinner />
            </div>
        );
    }

    if (!user) return null;

    return (
        <div className="reserved-dashboard-container">
            <Toast ref={toast} position="top-right" />
            <header className="reserved-header">
                <div className="header-left">
                    <img src="/images/Logo_AVIS.png" alt="Logo" className="reserved-logo" />
                    <span className="area-title">Area Riservata OpenAvis</span>
                </div>
                <div className="header-right">
                    <div className="user-info">
                        <span className="user-role">{user.details}</span>
                        <Avatar icon="pi pi-user" shape="circle" style={{ backgroundColor: '#e63946', color: '#ffffff' }} />
                    </div>
                </div>
            </header>

            <div className="reserved-layout">
                <aside className="reserved-sidebar">
                    <Menu model={items} className="w-full border-none shadow-none bg-transparent" />
                </aside>

                <main className="reserved-content">
                    <div className="content-card p-4">
                        {activeSection === 'candidati' && hasRole('candidates-manager') && (
                            <section className="dashboard-section">
                                <div className="toolbar-row">
                                    <div>
                                        <h2 className="section-title m-0"><i className="pi pi-users mr-2"></i>Gestione Aspiranti</h2>
                                        <p className="text-secondary mt-1">Gestisci gli aspiranti donatori e le convocazioni.</p>
                                    </div>
                                    <div className="toolbar-actions">
                                        <Button label="Nuovo Aspirante" icon="pi pi-plus" severity="danger" onClick={() => {
                                            setSelectedDonor({ firstName: '', lastName: '', email: '', localAvis: 'Merate', phase: 'non_convocato', createdAt: new Date().toISOString() });
                                            setDonorDialog(true);
                                        }} />
                                        <div className="barcode-input-wrapper">
                                            <i className="pi pi-barcode barcode-icon"></i>
                                            <InputText
                                                ref={barcodeInputRef}
                                                value={barcodeFilter}
                                                onChange={(e) => setBarcodeFilter(e.target.value)}
                                                placeholder="Codice fiscale..."
                                                className="barcode-input"
                                            />
                                            {barcodeFilter && (
                                                <Button
                                                    icon="pi pi-times"
                                                    rounded
                                                    text
                                                    severity="secondary"
                                                    onClick={() => {
                                                        setBarcodeFilter('');
                                                        barcodeInputRef.current?.focus();
                                                    }}
                                                    className="barcode-clear-btn"
                                                    tooltip="Cancella filtro"
                                                />
                                            )}
                                        </div>
                                        <Button icon="pi pi-refresh" rounded text onClick={fetchDonors} loading={loadingDonors} tooltip="Aggiorna" />
                                    </div>
                                </div>

                                {/* Tabbed View for Phases */}
                                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} className="phase-tabview">

                                    {/* Tab 1: Non ancora convocato */}
                                    <TabPanel header={tabHeaderTemplate('non_convocato', filteredNonConvocati.length)}>
                                        <div className="phase-tab-content">
                                            <div className="phase-description">
                                                <Tag severity="info" value="Non ancora convocato" icon="pi pi-clock" className="phase-tag" />
                                                <span>Aspiranti registrati in attesa di essere convocati per il primo incontro in sede AVIS.</span>
                                            </div>
                                            <DataTable value={filteredNonConvocati} loading={loadingDonors} paginator rows={10}
                                                className="p-datatable-sm shadow-1 border-round overflow-hidden"
                                                emptyMessage="Nessun aspirante da convocare."
                                                sortField="lastName" sortOrder={1}>
                                                <Column body={actionCell} style={{ width: '160px' }} header="Azioni" />
                                                <Column field="lastName" header="Cognome" body={(r) => <b>{r.lastName}</b>} sortable />
                                                <Column field="firstName" header="Nome" sortable />
                                                <Column field="taxCode" header="Codice Fiscale" sortable />
                                                <Column field="email" header="Email" />
                                                <Column field="localAvis" header="AVIS Comunale" sortable />
                                                <Column field="convocationStatus" header="Stato Conv." body={(r) => <Tag severity={getStatusSeverity(r.convocationStatus)} value={getStatusLabel(r.convocationStatus)} />} />
                                                <Column header="Convocazione" body={convocationCell} style={{ minWidth: '400px' }} />
                                            </DataTable>
                                        </div>
                                    </TabPanel>

                                    {/* Tab 2: Convocato */}
                                    <TabPanel header={tabHeaderTemplate('convocato', filteredConvocati.length)}>
                                        <div className="phase-tab-content">
                                            <div className="phase-description">
                                                <Tag severity="warning" value="Convocato" icon="pi pi-envelope" className="phase-tag" />
                                                <span>Aspiranti convocati per il primo incontro in sede AVIS. Usa il pulsante verde per spostarli in Idoneità.</span>
                                            </div>
                                            <DataTable value={filteredConvocati} loading={loadingDonors} paginator rows={10}
                                                className="p-datatable-sm shadow-1 border-round overflow-hidden"
                                                emptyMessage="Nessun aspirante convocato."
                                                sortField="convocationDate" sortOrder={-1}>
                                                <Column body={convocatoActionCell} style={{ width: '180px' }} header="Azioni" />
                                                <Column field="lastName" header="Cognome" body={(r) => <b>{r.lastName}</b>} sortable />
                                                <Column field="firstName" header="Nome" sortable />
                                                <Column field="taxCode" header="Codice Fiscale" sortable />
                                                <Column field="email" header="Email" />
                                                <Column field="localAvis" header="AVIS Comunale" sortable />
                                                <Column field="convocationDate" header="Data Convocazione"
                                                    body={(rowData) => rowData.convocationDate ? new Date(rowData.convocationDate).toLocaleString('it-IT') : '-'} sortable />
                                            </DataTable>
                                        </div>
                                    </TabPanel>

                                    {/* Tab 3: Idoneità */}
                                    <TabPanel header={tabHeaderTemplate('idoneita', filteredIdoneita.length)}>
                                        <div className="phase-tab-content">
                                            <div className="phase-description">
                                                <Tag severity="success" value="Idoneità" icon="pi pi-check-circle" className="phase-tag" />
                                                <span>Aspiranti inviati in ospedale per verificare l'idoneità. Da questo punto vengono tracciati da AvisNet.</span>
                                            </div>
                                            <DataTable value={filteredIdoneita} loading={loadingDonors} paginator rows={10}
                                                className="p-datatable-sm shadow-1 border-round overflow-hidden"
                                                emptyMessage="Nessun aspirante in fase di idoneità."
                                                sortField="lastName" sortOrder={1}>
                                                <Column body={idoneitaActionCell} style={{ width: '160px' }} header="Azioni" />
                                                <Column field="lastName" header="Cognome" body={(r) => <b>{r.lastName}</b>} sortable />
                                                <Column field="firstName" header="Nome" sortable />
                                                <Column field="taxCode" header="Codice Fiscale" sortable />
                                                <Column field="email" header="Email" />
                                                <Column field="localAvis" header="AVIS Comunale" sortable />
                                                <Column field="convocationDate" header="Data Convocazione"
                                                    body={(rowData) => rowData.convocationDate ? new Date(rowData.convocationDate).toLocaleString('it-IT') : '-'} />
                                            </DataTable>
                                        </div>
                                    </TabPanel>

                                    {/* Tab 4: Gestito Fuori Portale */}
                                    <TabPanel header={tabHeaderTemplate('gestito_esternamente', filteredGestitoEsternamente.length)}>
                                        <div className="phase-tab-content">
                                            <div className="phase-description">
                                                <Tag severity="secondary" value="Gestito Fuori Portale" icon="pi pi-check-square" className="phase-tag" />
                                                <span>Aspiranti contrassegnati come gestiti al di fuori del portale.</span>
                                            </div>
                                            <DataTable value={filteredGestitoEsternamente} loading={loadingDonors} paginator rows={10}
                                                className="p-datatable-sm shadow-1 border-round overflow-hidden"
                                                emptyMessage="Nessun aspirante gestito al di fuori del portale."
                                                sortField="lastName" sortOrder={1}>
                                                <Column body={actionCell} style={{ width: '160px' }} header="Azioni" />
                                                <Column field="lastName" header="Cognome" body={(r) => <b>{r.lastName}</b>} sortable />
                                                <Column field="firstName" header="Nome" sortable />
                                                <Column field="taxCode" header="Codice Fiscale" sortable />
                                                <Column field="email" header="Email" />
                                                <Column field="localAvis" header="AVIS Comunale" sortable />
                                                <Column field="managedExternallyAt" header="Data Gestione"
                                                    body={(rowData) => rowData.managedExternallyAt ? new Date(rowData.managedExternallyAt).toLocaleString('it-IT') : '-'} sortable />
                                            </DataTable>
                                        </div>
                                    </TabPanel>

                                </TabView>

                            </section>
                        )}

                        {activeSection === 'notizie' && hasRole('news-editor') && (
                            <ManageNews toastRef={toast} />
                        )}

                        {activeSection === 'sondaggi' && (
                            <section className="dashboard-section">
                                <SurveyManager userEmail={user?.details} />
                            </section>
                        )}
                    </div>
                </main>
            </div>

            <Dialog header="Dettagli Aspirante" visible={donorDialog} style={{ width: '50vw' }} onHide={() => setDonorDialog(false)}
                footer={<Button label="Salva" icon="pi pi-check" onClick={saveDonor} severity="danger" loading={savingDonor} />}>
                <div className="grid p-fluid">
                    {donorFields.map(field => (
                        <div key={field.name} className="field col-12 md:col-6 mb-3">
                            <label className="block mb-2 font-bold">{field.label}</label>
                            {field.type === 'text' || field.type === 'email' ? (
                                <InputText value={selectedDonor?.[field.name] || ''} onChange={(e) => updateDonorField(field.name, e.target.value)} />
                            ) : field.type === 'select' ? (
                                <Dropdown value={selectedDonor?.[field.name]} options={field.options} onChange={(e) => updateDonorField(field.name, e.value)} placeholder="Seleziona" showClear />
                            ) : field.type === 'date' ? (
                                <Calendar value={selectedDonor?.[field.name] ? new Date(selectedDonor[field.name]) : null} onChange={(e) => updateDonorField(field.name, e.value)} dateFormat="dd/mm/yy" showIcon />
                            ) : null}
                        </div>
                    ))}
                </div>
            </Dialog>
        </div>
    );
};

export default ReservedDashboard;
